import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { AffordabilityCalculatorForm, CalculatorPages } from '../types';
import { CalculatorBackButton } from '@components/common/CalculatorBackButton/CalculatorBackButton';
import { ConfirmDialog } from '@components/common/ConfirmDialog/ConfirmDialog';
import { Container } from '@components/common/Container';
import { CalculatorHeader } from '@pages/CalculatorPage/CalculatorHeader/CalculatorHeader';
import { ResultsTabsEnum } from './types';
import { ResultsPageQuestions } from './questions';
import { ResultsSection } from '@pages/CalculatorPage/ResultsPage/ResultsSection/ResultsSection';
import { clearAffordabilityCalculatorSavedData } from '@utils/localStorage';
import { ReviewPage } from '@pages/CalculatorPage/ReviewPage/ReviewPage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  filterTabsByScenario,
  getHeaderByScenario,
  getMaximumFinancing,
  getScenario,
  getSelectedResult,
  resultsFormatter,
  ResultsScenariosEnum,
  scenariosWithoutFooter,
  selectFailedTextOptionKey,
  siblingsProductRemover,
} from './helpers';
import { affordabilityCalculatorFormDefaultValues } from '../helpers';
import { scrollToTop, stageChecker } from '@utils/general';

export const ResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const form = useFormContext<AffordabilityCalculatorForm>();
  const formFields = form.watch();
  const { financeDetails, results, isTechIssue } = formFields;

  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleClosePopup = () => setIsPopupOpen(false);

  const handleOpenPopup = () => setIsPopupOpen(true);

  const handleConfirm = () => {
    handleClosePopup();
    clearAffordabilityCalculatorSavedData();
    form.reset(affordabilityCalculatorFormDefaultValues);
    navigate('/calculator');
    scrollToTop();
  };

  const handleCancel = () => handleClosePopup();

  const formattedResults = resultsFormatter(siblingsProductRemover(results));

  stageChecker(() => console.log('after siblings filter', formattedResults));

  const maximumAvailableFinancing = getMaximumFinancing(formattedResults);
  const scenario = isTechIssue
    ? ResultsScenariosEnum.techIssue
    : getScenario(formFields, maximumAvailableFinancing, formattedResults);
  const { bestProduct, productOptions } = getSelectedResult(financeDetails, formattedResults);

  stageChecker(() => console.log({ bestProduct, productOptions }));

  const defaultTab = scenario === ResultsScenariosEnum.unableToHelp ? ResultsTabsEnum.summary : ResultsTabsEnum.results;
  const [activeTab, setActiveTab] = useState(defaultTab);
  const isUnableToHelpScenario = scenario === ResultsScenariosEnum.unableToHelp;
  const tabs = [ResultsTabsEnum.summary];

  if (!isTechIssue && results) {
    tabs.unshift(ResultsTabsEnum.results);
  }

  const headerKey = getHeaderByScenario(scenario);
  const reason = selectFailedTextOptionKey(results.find(resultObj => !resultObj.result?.isPassed));
  const { tabsKey, newCalculationsKey, footnoteKeys } = ResultsPageQuestions;

  const getSectionByTab = (tab: ResultsTabsEnum) => {
    switch (tab) {
      case ResultsTabsEnum.results:
        return (
          <ResultsSection
            bestProduct={bestProduct}
            productOptions={productOptions}
            sortBy={sortBy}
            financeDetails={financeDetails}
            setSortBy={setSortBy}
          />
        );
      case ResultsTabsEnum.summary:
        return <ReviewPage isSubmitHidden />;
      default:
        return <></>;
    }
  };

  const goBack = () => {
    searchParams.set('section', CalculatorPages.reviewAnswers);
    setSearchParams(searchParams);
    scrollToTop();
  };

  return (
    <>
      <CalculatorHeader />
      <div className={'bg-dark xs:pt-8 xs:pb-10 flex content-center md:py-16'}>
        <Container maxWidth={'lg'} className={'flex flex-wrap content-center'}>
          <h1 className={'font-satoshi-black xs:text-2-5xl m-0 font-black text-white'}>
            <FormattedMessage
              id={isUnableToHelpScenario ? reason : headerKey}
              values={{ financingAmount: maximumAvailableFinancing }}
            />
          </h1>
        </Container>
      </div>
      <Container maxWidth={'lg'} className={'xs:py-9 md:py-14'}>
        <div className={'xs:w-full md:w-7/12'}>
          <div className={'border-fog flex border border-x-0 border-t-0 border-solid'}>
            {tabs
              .filter(tab => filterTabsByScenario(tab, scenario))
              .map((tab, index) => {
                if (scenario === ResultsScenariosEnum.unableToHelp && tab === ResultsTabsEnum.results) return null;

                const isActive = tab === activeTab;
                const activeClassName =
                  'bg-white text-main border border-solid border-fog border-b-white cursor-default font-black';
                const unactiveClassName =
                  'text-ash bg-paper border-fog border border-x-paper border-t-paper border-solid cursor-pointer';

                return (
                  <div
                    className={`xs:py-2 xs:px-4 -mb-px rounded-t-lg text-lg md:py-3 md:px-6
                    ${isActive ? activeClassName : unactiveClassName}
                    ${index > 0 && 'ml-2'}`}
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                  >
                    <FormattedMessage id={tabsKey + tab} />
                  </div>
                );
              })}
          </div>
          {getSectionByTab(activeTab)}
          <div className={'xs:flex-col-reverse xs:mt-10 flex w-full justify-between md:flex-row'}>
            <CalculatorBackButton onClick={goBack} isSvg={false} textKey={'cta.edit'} />
            <div className={'h-8 w-full md:hidden'}></div>
            <CalculatorBackButton isSvg={false} textKey={newCalculationsKey} onClick={handleOpenPopup} />
          </div>

          {scenariosWithoutFooter.includes(scenario) ? null : (
            <div className={'xs:text-center font-satoshi-medium text-ash mt-10 text-xs font-medium md:text-left'}>
              <p className={'m-0 mb-5'}>
                <FormattedMessage id={footnoteKeys.p1} />
              </p>
              <p className={'m-0 mb-5'}>
                <FormattedMessage id={footnoteKeys.p2} />
              </p>
              <p className={'m-0'}>
                <FormattedMessage id={footnoteKeys.p3} />
              </p>
            </div>
          )}
        </div>
      </Container>
      <ConfirmDialog
        titleKey='affordabilityCalculator.results.areYouSure'
        open={isPopupOpen}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onClose={handleClosePopup}
      />
    </>
  );
};
